/**
 * 메뉴 서비스
 *
 * 메뉴 관련 API 호출 및 데이터 처리
 */

import apiClient from './api';
import type {
  Menu,
  MenuListParams,
  CreateMenuRequest,
  UpdateMenuRequest,
  UpdateMenuOrderRequest,
} from '../types/menu';

/**
 * 메뉴 목록 조회 (계층 구조)
 *
 * @param params - 조회 파라미터
 * @returns 메뉴 목록 (계층 구조)
 */
export const getMenus = async (
  params?: MenuListParams
): Promise<{ success: boolean; data: { menus: Menu[] } }> => {
  const response = await apiClient.get('/admin/menus', { params });
  return response.data;
};

/**
 * 메뉴 상세 조회
 *
 * @param id - 메뉴 ID
 * @returns 메뉴 정보
 */
export const getMenu = async (
  id: number
): Promise<{ success: boolean; data: { menu: Menu } }> => {
  const response = await apiClient.get(`/admin/menus/${id}`);
  return response.data;
};

/**
 * 메뉴 생성
 *
 * @param data - 메뉴 생성 데이터
 * @returns 생성된 메뉴 정보
 */
export const createMenu = async (
  data: CreateMenuRequest
): Promise<{ success: boolean; data: { menu: Menu } }> => {
  const response = await apiClient.post('/admin/menus', data);
  return response.data;
};

/**
 * 메뉴 정보 수정
 *
 * @param id - 메뉴 ID
 * @param data - 수정할 데이터
 * @returns 수정된 메뉴 정보
 */
export const updateMenu = async (
  id: number,
  data: UpdateMenuRequest
): Promise<{ success: boolean; data: { menu: Menu } }> => {
  const response = await apiClient.patch(`/admin/menus/${id}`, data);
  return response.data;
};

/**
 * 메뉴 삭제
 *
 * @param id - 메뉴 ID
 * @returns 성공 메시지
 */
export const deleteMenu = async (
  id: number
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.delete(`/admin/menus/${id}`);
  return response.data;
};

/**
 * 메뉴 순서 일괄 업데이트
 *
 * @param data - 순서 업데이트 데이터
 * @returns 성공 메시지
 */
export const updateMenuOrder = async (
  data: UpdateMenuOrderRequest
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.put('/admin/menus/reorder', data);
  return response.data;
};

/**
 * 섹션 단위 순서 변경
 *
 * @param section - 이동할 섹션
 * @param direction - 이동 방향 ('up' 또는 'down')
 * @returns 성공 메시지
 */
export const updateSectionOrder = async (
  section: string,
  direction: 'up' | 'down'
): Promise<{ success: boolean; message: string }> => {
  // 명시적으로 headers를 설정하여 브라우저가 preflight를 강제로 발생시키도록 함
  // X-Requested-With 헤더는 preflight 강제 조건입니다
  const response = await apiClient.put(
    '/admin/menus/sections/reorder',
    {
      section,
      direction,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }
  );
  return response.data;
};
